<template>
  <q-card class="inhdialog-container">
    <div class="inhdialog-header">
      <h1 class="dialog_name">
        {{ $t('full_info_on') }} {{ isPayment ? $t('on_payment') : $t('on_invoice') }} "{{ isPayment ? payInfoContainer.Number : billInfoContainer.Number }}"
      </h1>
      <app-icon
        name="close"
        width="18px"
        color="#979797"
        hover-color="#499259"
        cursor="pointer"
        :title="$t('close_label')"
        @click="close"
      />
    </div>

    <div class="dialog_content">
      <!-- Пока данные загружаются -->
      <div v-if="dataLoading" class="loading-container q-mt-sm">
        <div v-for="n in 8" :key="n" class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <!-- Жирный заголовок (короткая широкая полоска) -->
            <div class="skeleton skeleton-title"></div>

            <!-- Текстовый спан (длинная полоска) -->
            <div
              class="skeleton skeleton-text"
              :class="{
                'skeleton-text-short': n % 3 === 0,
                'skeleton-text-medium': n % 3 === 1,
              }"
            ></div>
          </div>
        </div>
      </div>

      <div v-else class="inner_container">
        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div v-if="!isPayment" class="bold-name mr-xs">{{ $t('status_label') }}:</div>
            <span v-if="!isPayment">{{ getBillStatus }}</span>
            <div class="bold-name mr-xs">{{ $t('creation') }}<span v-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm && !editPaymentForm.date" class="required-asterisk">*</span><span v-else>:</span></div>
            <span v-if="!isPayment || !isPaymentEditMode || (isPayment && isPaymentEditMode && !payInfoContainer.CreatedInCrm)">{{ dateFormatted(isPayment ? payInfoContainer.Date : billInfoContainer.Date) }}</span>
            <VueDatePicker
              v-else-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm"
              utc="preserve"
              timezone="Europe/Moscow"
              :locale="$i18n.locale"
              :style="'width: 150px; min-width: 150px; max-width: 150px;'"
              :teleport="true"
              v-model="editPaymentForm.date"
              :enable-time-picker="false"
              :clearable="false"
              :required="true"
              :format="formatDatePicker"
              :text-input="datePickerTextInputOptions"
            />
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left items-baseline">
            <div class="bold-name">{{ $t('invoice_type') }}<span v-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm && (editPaymentForm.type == null || editPaymentForm.type === undefined)" class="required-asterisk">*</span><span v-else>:</span></div>
            <span v-if="!isPayment || !isPaymentEditMode || (isPayment && isPaymentEditMode && !payInfoContainer.CreatedInCrm)">{{ financeType(isPayment ? payInfoContainer.Type : billInfoContainer.Type) }}</span>
            <q-select
              v-else-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm"
              flat
              v-model="editPaymentForm.type"
              style="min-width: 150px; padding: 0 !important;"
              class="thin-input"
              dropdown-icon="expand_more"
              fill-input
              :bottom-slots="false"
              dense
              outlined
              emit-value
              map-options
              option-label="Name"
              option-value="Id"
              :options="paymentTypeOptions"
            />
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left items-center">
            <div class="bold-name">{{ $t('counterparty') }}:</div>
            <span>{{ isPayment ? payInfoContainer.Counterparty?.Name : billInfoContainer.Counterparty?.Name }}</span>
            <template v-if="isPayment ? payInfoContainer.Counterparty?.Id > 0 : billInfoContainer.Counterparty?.Id > 0">
              <app-icon
                class="q-mx-xs"
                name="info-circle-outline"
                width="18px"
                color="#979797"
                hover-color="#499259"
                cursor="pointer"
                :title="$t('information')"
                @click="showCPartyInfoMode = true"
              />
              <q-dialog
                v-model="showCPartyInfoMode"
                no-shake
                :full-width="true"
              >
                <counterparty-info-component
                  :counterparty-id="isPayment ? payInfoContainer.Counterparty?.Id : billInfoContainer.Counterparty?.Id"
                  @close="showCPartyInfoMode = false"
                />
              </q-dialog>
            </template>
          </div>
        </div>

        <div v-if="isPayment && payInfoContainer.Creator" class="field-group">
          <div class="field-group__row no-wrap justify-left items-baseline">
            <div class="bold-name">{{ $t('creator') }}:</div>
            <span>{{ payInfoContainer.Creator.Name }}</span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left items-baseline">
            <div class="bold-name">{{ $t('number') }}<span v-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm && !String(editPaymentForm.number ?? '').trim()" class="required-asterisk">*</span><span v-else>:</span></div>
            <span v-if="!isPayment || !isPaymentEditMode || (isPayment && isPaymentEditMode && !payInfoContainer.CreatedInCrm)">{{ isPayment ? payInfoContainer.Number : billInfoContainer.Number }}</span>
            <native-input
              v-else-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm"
              style="min-width: 150px"
              v-model="editPaymentForm.number"
              :show-border="true"
              :disabled-field="false"
              :edit-on-click="true"
            />
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left items-baseline">
            <div class="bold-name">{{ $t('amount') }}<span v-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm && (Number(editPaymentForm.amount) < 0 || isNaN(Number(editPaymentForm.amount)))" class="required-asterisk">*</span><span v-else>:</span></div>
            <span v-if="!isPayment || !isPaymentEditMode || (isPayment && isPaymentEditMode && !payInfoContainer.CreatedInCrm)">{{ isPayment ? money(payInfoContainer.CalculatedAmount) : money(billInfoContainer.Amount) }} {{ isPayment ? payInfoContainer.Currency : billInfoContainer.Currency }}</span>
            <template v-else-if="isPayment && isPaymentEditMode && payInfoContainer.CreatedInCrm">
              <native-input
                style="min-width: 120px"
                v-model="editPaymentForm.amount"
                input-type="number"
                :show-border="true"
                :disabled-field="false"
                :edit-on-click="true"
                :min-number-value="0"
              />
              <span class="q-mx-xs">{{ editPaymentForm.currency }}</span>
            </template>
            <div v-if="!isPayment" class="mr-xs">{{ getPayStatusText }}</div>
            <div v-if="!isPayment" class="file-button q-ml-sm">
              <div
                class="q-mx-xs col"
                v-if="
                  billInfoContainer.Connections &&
                  billInfoContainer.Connections.length
                "
                style="display: vertical-align: middle; overflow: hidden; height: 16px; width: 200px; border-right: 1px solid black; border-bottom: 1px solid black;"
              >
                <payment-progress
                  :narrow="true"
                  :width="200"
                  :percent="Math.round(billInfoContainer.PayedPercent)"
                />
                <div
                  style="
                    height: 9px;
                    margin-top: -13px;
                    vertical-align: middle;
                    display: inline-flex;
                    flex-direction: row;
                  "
                  class="nowrap"
                >
                  <payment-progress
                    v-for="(conn, pr_ind) in billInfoContainer.Connections"
                    :key="'c_' + pr_ind"
                    :percent="Math.round(conn.PayedPercent)"
                    :allow-null-width="true"
                    :width="
                      (200 * Math.round(conn.AmountPercent)) / 100 -
                      (billInfoContainer.Connections.length > 1
                        ? pr_ind === 0 ||
                          pr_ind === billInfoContainer.Connections.length - 1
                          ? 0
                          : 1
                        : 0) +
                      2
                    "
                    :style="`margin-left: ${pr_ind === 0 ? 0 : '-2px'};`"
                    :narrow="true"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isPayment && isPaymentEditMode" class="field-group">
          <div class="field-group__row no-wrap justify-left items-baseline">
            <div class="bold-name">{{ $t('currency') }}<span v-if="!String(editPaymentForm.currency ?? '').trim()" class="required-asterisk">*</span><span v-else>:</span></div>
            <native-input
              style="min-width: 150px"
              v-model="editPaymentForm.currency"
              :show-border="true"
              :disabled-field="false"
              :edit-on-click="true"
            />
          </div>
        </div>
        <div v-if="isPayment && isPaymentEditMode" class="field-group">
          <div class="field-group__row no-wrap justify-left items-baseline">
            <div class="bold-name">{{ $t('currency_rate') }}:</div>
            <native-input
              style="min-width: 150px"
              v-model="editPaymentForm.currencyRate"
              input-type="number"
              :show-border="true"
              :disabled-field="false"
              :edit-on-click="true"
              :min-number-value="0"
            />
          </div>
        </div>

        <div v-if="billInfoContainer.HasPayments && !isPayment" class="field-group">
          <div class="bold-name">{{ $t('payments') }}:</div>
          <div class="file-list">
            <span
              v-for="(p, pi) in billInfoContainer.Payments"
              :key="'paym_' + pi"
            >
              {{ pi + 1 }}) {{ financeType(p.Type) }},
              {{ p.Counterparty.Name }}, {{ dateFormatted(p.Date) }},
              {{ money(p.TotalAmount || 0) }} {{ p.Currency }}
              <span v-if="p.PaymentPercent != null">
                ({{ roundTo(p.PaymentPercent, 2) }}%)
              </span>
            </span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left items-center">
            <div class="bold-name">{{ $t('comment') }}:</div>
            <span
              v-if="isPayment && isPaymentEditMode && !canEditPaymentNote"
              class="q-ml-xs inline-flex"
            >
              <app-icon
                name="info-circle-outline"
                width="18px"
                color="#979797"
              />
              <q-tooltip :delay="300" anchor="top middle" self="center middle">
                {{ paymentNoteEditHint }}
              </q-tooltip>
            </span>
            <native-input
              v-if="!(isPayment && isPaymentEditMode && !canEditPaymentNote)"
              ref="comment_field"
              style="width: -moz-available; width: -webkit-fill-available;"
              class="q-mx-sm"
              :disabled-field="isPayment ? !canEditPaymentNote : true"
              :show-border="isPayment ? isPaymentEditMode : false"
              :edit-on-click="canEditPaymentNote"
              :model-value="isPayment && isPaymentEditMode ? editPaymentForm.note : (isPayment ? payInfoContainer.Note : billInfoContainer.Note)"
              @update:modelValue="onPaymentNoteUpdate"
            />
          </div>
        </div>
        <div class="field-group">
          <div class="bold-name flex items-center">
            {{ $t('linked') }}:
            <!-- <div class="file-button q-ml-sm">
              <q-icon
                name="edit"
                size="18px"
                class="pointer hover-green"
                title="Редактировать"
              />
            </div> -->
          </div>
          <div class="file-list">
            <!-- <movement-row v-for="(conn, i) in billInfoContainer?.Connections?.filter(el => el.Descriptor === 1)" :key="'conn_'+i" :el="conn.Income" /> -->
            <connections-table-html
              ref="connectionsTableRef"
              v-if="tmp_connections.length"
              v-model="tmp_connections"
              @removeItem="removeConnection"
              @checkError="onCheckError"
              :max-amount="billInfoContainer.CalculatedAmount"
              :max-payed="billInfoContainer.PayedAmount"
              :hide-remove-btn="isPayment"
              :show-non-existent="!isPayment"
              :check-sums="!isPayment"
              :show-sums="!isPayment"
              @recalc="writeConnections"
            />
            <span class="text-caption" style="text-align: right; color: red">{{
              errorText
            }}</span>
            <span v-if="thisConnCached" class="text-caption" style="text-align: right; color: green">
              {{ $t('new_bindings_until_save') }}
            </span>
          </div>
        </div>

        <div class="field-group">
          <div class="field-group__row no-wrap justify-left">
            <div class="bold-name">{{ $t('one_c_instance') }}:</div>
            <span>{{ isPayment ? payInfoContainer.Instance1C : billInfoContainer.Instance1C }}</span>
          </div>
        </div>

      </div>
    </div>
    <!-- Footer: как в MovementCreateComponent -->
    <div v-if="isPayment" class="field-group q-mb-md">
      <div class="row no-wrap justify-end">
        <q-btn
          no-caps
          unelevated
          color="primary"
          :label="isPaymentEditMode ? (paymentSaveLoading ? $t('loading') + '...' : $t('save')) : $t('edit')"
          :disable="paymentSaveLoading || (isPaymentEditMode && !canSavePaymentEdit)"
          @click.stop="isPaymentEditMode ? savePaymentEdit() : startPaymentEdit()"
        />
        <q-btn
          v-if="isPaymentEditMode"
          :label="$t('cancel')"
          no-caps
          flat
          @click.stop="cancelPaymentEdit"
        />
      </div>
    </div>
  </q-card>
</template>

<script lang="ts">
import { Vue, Component, Prop, Emit, Watch } from "vue-facing-decorator";
import { QBtn, QIcon, QSelect } from "quasar";
// import ParameterInline from './ParameterInline.vue';
import { reactive } from "vue";
import {
  BillConnectionWithValuesTypeDescriptor,
  PayStatus,
} from "@/models/etc/types";
import {
  BillConnectionWithValues,
  BillConnectionWithValuesEditAsker,
} from "@/models/interfaces/storage/storage-models";
import { BillType } from "@/models/etc/types";
import { financesStore } from "../../../../store/finances";
import {
  BillConnectionToItemIncomeWithValues,
  BillConnectionToProjectWithValues,
  BillConnectionWithValuesBase,
  BillInfo,
  BillSearchedWithConnections,
  PaymentInfo,
} from "../../../../models/interfaces/finances/finance-models";
import type { PaymentEditAsker } from "../../../../models/interfaces/finances/finance-models";
import ConnectionsTableHtml from "../ConnectionsTableHtml.vue";
import NativeInput from "../../../common/basic/NativeInput.vue";
import PaymentProgress from "../storageshared/PaymentProgress.vue";
import CounterpartyInfoComponent from "./CounterpartyInfoComponent.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import {
  ConnectionLine,
  useEntityStore,
} from "../../../../store/heartbeat-workers/useEntityStore";
import { roundTo } from "../../../../utils/auxfunc";
import { intlLocale } from "@/utils/format";

@Component({
  components: {
    QBtn,
    QIcon,
    QSelect,
    ConnectionsTableHtml,
    NativeInput,
    PaymentProgress,
    CounterpartyInfoComponent,
    AppIcon,
  },
  emits: ["connections_changed", "close"],
})
export default class FinanceInfoComponent extends Vue {
  declare $refs: {
    fileInputStorage: HTMLInputElement;
  };

  @Prop({ type: Number, default: 0 }) readonly billId!: number;
  @Prop({ type: Boolean, default: false }) readonly isPayment!: boolean;

  /** Были ли изменения (редактирование платежа или привязок), чтобы при закрытии обновить данные в ProjectFinanceModule */
  hadChanges: boolean = false;
  private _closeEmitted: boolean = false;

  // stores import
  finStore: any;
  entityStore: any;
  errorText: string = "";
  showCPartyInfoMode: boolean = false;

  billInfoContainer: BillInfo = reactive({} as BillInfo);
  payInfoContainer: PaymentInfo = reactive({} as PaymentInfo);
  tmp_connections: ConnectionLine[] = reactive([]);

  dataLoading: boolean = false;
  thisConnCached: ConnectionLine | undefined = undefined;

  isPaymentEditMode: boolean = false;
  paymentSaveLoading: boolean = false;
  editPaymentForm = reactive({
    amount: 0,
    number: '',
    note: '',
    date: new Date() as Date,
    type: BillType.Incoming as BillType,
    currency: '',
    currencyRate: 0,
  });

  roundTo(val: number, places: number) {
    return roundTo(val, places);
  }

  created() {
    this.finStore = financesStore();
    this.entityStore = useEntityStore();
  }

  mounted() {
    if (!this.isPayment) {
      this.loadBillInfo(this.billId);
    } else {
      this.loadPaymentInfo(this.billId);
    }
  }

  /** Форматирование даты по UTC (дата с бэка приходит в UTC, чтобы не сдвигалась на следующий день) */
  dateFormatted(val: string): string {
    if (!val) return '';
    const d = new Date(val);
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}.${month}.${year}`;
  }

  financeType(ft: number): string {
    return ft ? this.$t('outgoing') : this.$t('incoming');
  }

  get getBillStatus(): string {
    return this.billInfoContainer.IsDraft || this.billInfoContainer.CreatedInCrm
      ? this.$t('draft')
      : this.$t('approved_accountant');
  }

  get getPayStatusText(): string {
    switch (this.billInfoContainer.PayStatus) {
      case PayStatus.Cancelled:
        return this.$t('cancelled');
      case PayStatus.NotPayed:
        return this.$t('not_paid');
      case PayStatus.PartlyPayed:
        return this.$t('partially_paid');
      case PayStatus.Payed:
        return this.$t('paid');
      case PayStatus.Unknown:
        return this.$t('unknown');
      default:
        return "";
    }
  }

  // money display value formatting
  money(n: number) {
    return new Intl.NumberFormat(intlLocale(), {
      // style: "currency",
      // currency: "RUB",
      minimumFractionDigits: 0, // Не показывать нули, если дробной части нет
      maximumFractionDigits: 4,
    }).format(n || 0);
  }

  onCheckError(val: any) {
    this.errorText = val.message;
  }

  get paymentTypeOptions() {
    return [
      { Name: this.$t('incoming'), Id: BillType.Incoming },
      { Name: this.$t('outgoing'), Id: BillType.Outgoing },
    ];
  }

  formatDatePicker(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  }

  get datePickerTextInputOptions() {
    return {
      enterSubmit: true,
      tabSubmit: true,
      openMenu: 'toggle',
      rangeSeparator: '-',
      selectOnFocus: false,
      format: 'dd.MM.yyyy',
    };
  }

  onPaymentNoteUpdate(val: string) {
    if (this.isPayment && this.isPaymentEditMode) {
      this.editPaymentForm.note = val ?? '';
    }
  }

  startPaymentEdit() {
    if (!this.isPayment || !this.payInfoContainer?.Id) return;
    this.editPaymentForm.amount = this.payInfoContainer.CalculatedAmount ?? this.payInfoContainer.Amount ?? 0;
    this.editPaymentForm.number = this.payInfoContainer.Number ?? '';
    this.editPaymentForm.note = this.payInfoContainer.Note ?? '';
    this.editPaymentForm.date = this.payInfoContainer.Date ? new Date(this.payInfoContainer.Date) : new Date();
    this.editPaymentForm.type = this.payInfoContainer.Type ?? BillType.Incoming;
    this.editPaymentForm.currency = this.payInfoContainer.Currency ?? '';
    this.editPaymentForm.currencyRate = this.payInfoContainer.CurrencyRate ?? 0;
    this.isPaymentEditMode = true;
  }

  cancelPaymentEdit() {
    if (this.isPayment) this.isPaymentEditMode = false;
  }

  /** При CreatedInCrm можно менять всё; при !CreatedInCrm — только валюта и курс (валюта не пустая) */
  get canSavePaymentEdit(): boolean {
    if (!this.isPayment || !this.payInfoContainer?.Id) return false;
    if (!String(this.editPaymentForm.currency ?? '').trim()) return false;
    if (!this.payInfoContainer.CreatedInCrm) return true;
    const n = Number(this.editPaymentForm.amount);
    if (isNaN(n) || n < 0) return false;
    if (!String(this.editPaymentForm.number ?? '').trim()) return false;
    if (!this.editPaymentForm.date) return false;
    if (this.editPaymentForm.type === undefined || this.editPaymentForm.type === null) return false;
    return true;
  }

  get canEditPaymentNote(): boolean {
    return this.isPayment && this.isPaymentEditMode && !!this.payInfoContainer?.CreatedInCrm;
  }

  get paymentNoteEditHint(): string {
    return this.$t('payment_note_edit_unavailable_not_created_in_system');
  }

  async savePaymentEdit() {
    if (!this.isPayment || !this.payInfoContainer?.Id || !this.canSavePaymentEdit) return;
    this.paymentSaveLoading = true;
    const canEditAll = this.payInfoContainer.CreatedInCrm;
    const currency = (String(this.editPaymentForm.currency ?? '').trim() || this.payInfoContainer.Currency) ?? '';
    const rawRate = this.editPaymentForm.currencyRate;
    const rateNum = Number(rawRate);
    const currencyRate = (typeof rateNum === 'number' && !isNaN(rateNum) && rateNum > 0) ? rateNum : 1;
    const arg: PaymentEditAsker = {
      PaymentId: this.payInfoContainer.Id,
      Amount: canEditAll ? (Number(this.editPaymentForm.amount) || 0) : (this.payInfoContainer.CalculatedAmount ?? this.payInfoContainer.Amount ?? 0),
      Number: canEditAll ? String(this.editPaymentForm.number ?? '').trim() : (this.payInfoContainer.Number ?? ''),
      Date: canEditAll
        ? (this.editPaymentForm.date instanceof Date ? this.editPaymentForm.date : new Date(this.editPaymentForm.date))
        : (this.payInfoContainer.Date ? new Date(this.payInfoContainer.Date) : new Date()),
      Currency: currency,
      CurrencyRate: currencyRate,
      ...(canEditAll
        ? { Note: String(this.editPaymentForm.note ?? '').trim(), Type: this.editPaymentForm.type }
        : { Note: null, Type: this.payInfoContainer.Type }),
    };
    await this.finStore.paymentEdit(arg);
    this.paymentSaveLoading = false;
    this.isPaymentEditMode = false;
    this.hadChanges = true;
    await this.loadPaymentInfo(this.payInfoContainer.Id, true);
  }

  async loadBillInfo(billNum: number, no_indication: boolean = false) {
    this.errorText = "";
    this.dataLoading = !no_indication;

    this.billInfoContainer = (await this.finStore.getInfoCBill(
      billNum
    )) as BillInfo;

    // если этот счёт уже есть в кэше (он только добавляется к новому приходу), то брать именно его привязки, чтобы
    // если они были изменены и не сохранены в данном окне (в таком случае запрос на изменение привязок не идёт сразу, а идёт в общем createIncome),
    // то при повторном открытии окна значения не сбрасывались на первоначальные (поскольку сохранения в БД не было)
    this.thisConnCached = this.entityStore.accounts.find(
      (el: ConnectionLine) =>
        el.bill?.Id === this.billId &&
        !!(el.bill as BillSearchedWithConnections)?.Connections
    )

    let conn_array: BillConnectionWithValuesBase[] = [];

    if (this.thisConnCached) {
      conn_array = (this.thisConnCached.bill as BillSearchedWithConnections)?.Connections;
    } else if (this.billInfoContainer?.Connections) {
      conn_array = this.billInfoContainer?.Connections;
    }

    if (conn_array) {
      this.tmp_connections = conn_array.map((conn) => {
        if (conn.Descriptor === 1) {
          const c = conn as BillConnectionToItemIncomeWithValues;
          return {
            conn_id: c.Id,
            percent: c.AmountPercent,
            amount: c.AbsoluteAmount,
            debit: c.PayedAmount,
            movement: c.Income,
            canEditAmounts: false,
            canEditDebits: true,
            canBeDeleted: false,
          } as ConnectionLine;
        } else {
          const c = conn as BillConnectionToProjectWithValues;
          return {
            conn_id: c.Id,
            percent: c.AmountPercent,
            amount: c.AbsoluteAmount,
            debit: c.PayedAmount,
            project: c.Project,
            canEditAmounts: true,
            canEditDebits: true,
            canBeDeleted: true,
          } as ConnectionLine;
        }
      });

      // дополнительная привязка добавляется только для нового создаваемого прихода, поэтому стоит условие nonExistent = true
      this.tmp_connections = reactive([
        ...this.tmp_connections,
        ...this.entityStore.accounts.filter(
          (el: ConnectionLine) => (el.bill?.Id === this.billId && el.nonExistent === true)
        )
      ]);
    }

    this.dataLoading = false;
  }

  async loadPaymentInfo(payNum: number, no_indication: boolean = false) {
    this.errorText = "";
    this.dataLoading = !no_indication;

    this.payInfoContainer = (await this.finStore.getInfoPayment(
      payNum
    )) as PaymentInfo;

    if (this.payInfoContainer?.Project) {
      const c = this.payInfoContainer?.Project;
      this.tmp_connections = [
        {
          conn_id: c.Id,
          percent: 0,
          amount: 0,
          debit: 0,
          project: c,
          canEditAmounts: false,
          canEditDebits: false,
          canBeDeleted: false,
        } as ConnectionLine
      ]
    }

    if (this.payInfoContainer?.StorageItemIncome) {
      const c = this.payInfoContainer?.StorageItemIncome;
      this.tmp_connections = [
        {
          conn_id: c.Id,
          percent: 0,
          amount: 0,
          debit: 0,
          movement: c,
          canEditAmounts: false,
          canEditDebits: false,
          canBeDeleted: false,
        } as ConnectionLine
      ]
    }

    this.dataLoading = false;
  }

  removeConnection(row: ConnectionLine) {
    if (!row || row.nonExistent === true) {
      return;
    }
    const realIdx = this.tmp_connections.indexOf(row);
    if (realIdx < 0) return;

    this.errorText = "";
    this.tmp_connections.splice(realIdx, 1);

    this.writeConnections();
  }

  async writeConnections() {
    if (this.isPayment) { return }

    this.errorText = "";
    // @ts-ignore
    const tableRef = this.$refs.connectionsTableRef as { runValidation?: () => boolean } | undefined;
    if (tableRef?.runValidation && !tableRef.runValidation()) {
      return;
    }

    const real_conn_array = this.tmp_connections
      .filter((el) => el.nonExistent !== true)
      .filter(
        (acc: ConnectionLine) =>
          acc.movement !== undefined || acc.project !== undefined
      );

    // update max possible amounts in the newly created movements
    // const newConnIdx = this.tmp_connections.findIndex(
      // (el) => el.nonExistent === true
    // );
    
    if (/*newConnIdx >= 0*/this.thisConnCached) {
      const conn_el = this.thisConnCached; // this.tmp_connections[newConnIdx];

      // Максимумы считаем по остальным привязкам, исключая текущую
      const other_connections = real_conn_array.filter(conn => conn.conn_id > 0 && 
        conn.movement?.Id !== this.thisConnCached?.movement?.Id );

      let connections_sum = 0;
      let max_percent = 100;
      let max_debit = 0;
      let max_amount = 0;

      for (let ind = 0; ind < other_connections.length; ind++) {
        connections_sum += other_connections[ind].amount;
        max_percent -= other_connections[ind].percent;
      }

      max_amount = this.billInfoContainer.Amount - connections_sum;
      max_debit =
        this.billInfoContainer.PayedAmount > connections_sum
          ? this.billInfoContainer.PayedAmount - connections_sum
          : 0;

      conn_el.maxAmount = this.billInfoContainer.Amount;
      conn_el.limitAmount = +roundTo(+max_amount, 6);
      conn_el.maxDebit = +max_debit;
      conn_el.maxPercent = +roundTo(+max_percent, 4);
      // conn_el.percent = +max_percent;
      if (conn_el.amount > +conn_el.limitAmount) {
        conn_el.amount = conn_el.limitAmount;
      }
      if (conn_el.debit > +max_debit) {
        conn_el.debit = +max_debit;
      }
      conn_el.canEditAmounts = conn_el.limitAmount > 0 || conn_el.maxPercent > 0;
      conn_el.canEditDebits = max_debit > 0;
      conn_el.canBeDeleted = false;

      // update other connections in this bill without request in case the user cancels changes
      const connBill = conn_el.bill as BillSearchedWithConnections;
      if (connBill) {
        connBill.Connections = real_conn_array.map((acc: ConnectionLine) => {
          return {
            Id: acc.conn_id,
            AbsoluteAmount: acc.amount, // decimal!
            AmountPercent: acc.percent, // double
            PayedAmount: acc.debit, // decimal!
            Descriptor: acc.movement
              ? BillConnectionWithValuesTypeDescriptor.Income
              : BillConnectionWithValuesTypeDescriptor.Project,
            Income: acc.movement,
            Project: acc.project
          } as any as BillConnectionWithValuesBase;
        });
        this.hadChanges = true;
        this.$emit("connections_changed", null);
      }
    } else {
      const request: BillConnectionWithValuesEditAsker = {
        BillId: this.billId,
        // dont save the non-existent connection; it's being changed directly in its store;
        Connections: real_conn_array.map((acc: ConnectionLine) => {
          return {
            Id: acc.conn_id,
            AbsoluteAmount: acc.amount, // decimal!
            AmountPercent: acc.percent, // double
            PayedAmount: acc.debit, // decimal!
            Descriptor: acc.movement
              ? BillConnectionWithValuesTypeDescriptor.Income
              : BillConnectionWithValuesTypeDescriptor.Project,
          } as BillConnectionWithValues;
        }),
      };
      const res = await this.finStore.updateConnectionValues(request);
      if (res.response_ok) {
        this.hadChanges = true;
        this.$emit("connections_changed", null);
        await this.loadBillInfo(this.billId, true);
      }
    }
  }

  close() {
    if (this._closeEmitted) return;
    this._closeEmitted = true;
    this.$emit("close", { hadChanges: this.hadChanges });
  }

  beforeUnmount() {
    if (this._closeEmitted) return;
    this.$emit("close", { hadChanges: this.hadChanges });
  }
}
</script>

<style lang="scss" scoped>
.inline-flex {
  display: inline-flex;
  align-items: flex-start;
}
.required-asterisk {
  color: red;
  margin-left: 0.2em;
  margin-right: 0.2em;
}
</style>
